import React, { Component } from 'react';
import { flowRight as compose } from 'lodash';
import { connect, ConnectedProps } from 'react-redux';
import { ELSButton } from '@els/els-component-button-react';
import { ELSIcon } from '@els/els-component-form-field-react';
import { Flyout } from '@els/els-react--flyout';
import { ELSTokenHelper } from '@els/els-ui-common-react';
import { studySelectors } from '../../../redux/student-study/studentStudy.selectors';
import { AssessmentTopicTableRow } from '../eaq-student-performance-details.models';
import { ELSButtonSize, ELSButtonType } from '../../../models/button.models';
import { ELSDataTable } from '../../../components/els.components';
import { AssessmentDto, AssessmentTypeDto } from '../../../apis/eols-assessment-service/eols-assessment-service.dtos';
import { getGoalBasedOnMasteryLevel } from '../../eaq-student-study/eaq-student-study-common-util.utilities';
import { EaqAssessmentConfig } from '../../eaq-student-topic-report/eaq-student-topic-report.models';
import { AnalyticsAction } from '../../../models/analytics.models';
import { createAssessment } from '../../../apis/eaq-app-facade-service/eaq-app-facade-service.utilities';
import {
  convertMasteryScoreToPercentage,
  getFormattedMasteryLevel,
  getMasteryLevelLabel,
  navigateToAssessmentPlayer
} from '../../../utilities/eaq.utilities';
import { getParentLinkId } from '../../../utilities/app.utilities';
import { RoutePath } from '../../../components/app/app.constants';
import { locationActions } from '../../../redux/location/location.actions';
import { studyActions } from '../../../redux/student-study/studentStudy.actions';
import { FlexLayout } from '../../../components/flex/FlexLayout.component';
import { FlexLayoutModifier } from '../../../components/flex/flex.constants';
import { FlexItem } from '../../../components/flex/FlexItem.component';
import MasteryChart from '../../eaq-student-topic-report/MasteryChart.component';
import { FlyoutPlacements } from '../../../models/ui.model';

export interface AssessmentTopicTablePropsOnly {
  isMultipleTopic: boolean;
  assessmentId: number;
  assessmentTopicRows: {};
  isbn: string;
}

const mapStateToProps = state => ({
  messages: studySelectors.getMessages(state),
  course: studySelectors.getCourse(state),
  appLinkData: studySelectors.getLinkData(state),
  appLinkCookies: studySelectors.getAppLinkCookies(state),
});

const mapDispatchToProps = {
  redirect: locationActions.redirect,
  navigateToApp: studyActions.navigateToApp,
  returnAppLink: studyActions.returnAppLink,
  trackAction: studyActions.trackAction,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export type AssessmentTopicTableProps = PropsFromRedux & AssessmentTopicTablePropsOnly;

class AssessmentTopicTable extends Component<AssessmentTopicTableProps> {
  getMasteryLevelColumn = (topicMasteryLevel: number) => {
    const { messages } = this.props;

    return (
      <div className="o-els-flex-layout o-els-flex-layout--middle o-els-flex-layout--center o-els-flex-layout--gutters-1o2 o-els-flex-layout--wrap">
        <div className="o-els-flex-layout__item c-ssa-eaq-assessment-topic-table__chart-label  u-els-text-right">
          {getFormattedMasteryLevel(AssessmentTypeDto.MASTERY, topicMasteryLevel, messages)}
        </div>

        <div className="o-els-flex-layout__item c-ssa-eaq-assessment-topic-table__chart">
          <button
            type="button"
            className="u-els-debuttonize u-default-cursor"
            disabled
            aria-label={getMasteryLevelLabel(topicMasteryLevel, this.props.messages)}
          >
            <div className="u-ssa-min-width-2x">
              <MasteryChart scoreAsPercentage={convertMasteryScoreToPercentage(topicMasteryLevel)} />
            </div>
          </button>
        </div>
      </div>
    );
  };

  getMasteryLevelHeading = () => {
    return (
      <Flyout
        flyout={this.getMasteryLevelLegend()}
        width="400px"
        placement={FlyoutPlacements.BOTTOM_START}
        className="c-ssa-eaq-assessment-topic-table__key-chart-info-button"
      >
        <button
          type="button"
          className="u-els-debuttonize u-els-anchorize"
          aria-label="Legend of Mastery Levels"
          data-testid="ssa-assessment-topic-table-mastery-legend-button"
        >
          <ELSIcon name="information-solid-circle" prefix="gizmo" />
        </button>
      </Flyout>
    );
  };

  getMasteryLevelLegend = () => {
    const { messages } = this.props;

    const masteryLevels = [
      { keyClass: 'c-ssa-mastery-chart-key-table__key--level-0', title: messages.INCOMPLETE, description: messages.INCOMPLETE_DESCRIPTION },
      { keyClass: 'c-ssa-mastery-chart-key-table__key--level-1', title: messages.NOVICE, description: messages.NOVICE_DESCRIPTION },
      { keyClass: 'c-ssa-mastery-chart-key-table__key--level-2', title: messages.INTERMEDIATE, description: messages.INTERMEDIATE_DESCRIPTION },
      { keyClass: 'c-ssa-mastery-chart-key-table__key--level-3', title: messages.PROFICIENT, description: messages.PROFICIENT_DESCRIPTION },
    ];

    return (
      <FlexLayout modifiers={[FlexLayoutModifier.MIDDLE]}>
        <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
          <FlexLayout modifiers={[FlexLayoutModifier.LEFT, FlexLayoutModifier.VERTICAL_GUTTERS]}>
            <FlexItem>
              <h3>{messages.MASTERY_LEVELS}</h3>
            </FlexItem>
          </FlexLayout>
          {masteryLevels.map((level) => (
            <FlexLayout key={level.title}>
              <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
                <FlexLayout
                  modifiers={[
                    FlexLayoutModifier.LEFT,
                    FlexLayoutModifier.GUTTERS,
                    FlexLayoutModifier.VERTICAL_GUTTERS]}>
                  <FlexItem>
                    <div className={`c-ssa-mastery-chart-key-table__key ${level.keyClass}`} />
                  </FlexItem>
                  <FlexItem>
                    <b>{level.title}</b>
                    <p>{level.description}</p>
                  </FlexItem>
                </FlexLayout>
              </FlexItem>
            </FlexLayout>
          ))}
        </FlexItem>
      </FlexLayout>
    );
  };

  handleQuizMeClick = (topicRow: AssessmentTopicTableRow): Promise<void> | void => {
    const {
      assessmentId,
      navigateToApp,
      course,
      appLinkData,
      appLinkCookies,
      trackAction,
      isbn
    } = this.props;

    const { topicName, topicVtwId } = topicRow;
    const goal = getGoalBasedOnMasteryLevel(topicRow.masteryLevel);
    const assessmentConfig: EaqAssessmentConfig = {
      assessmentGoals: [{ goal, vtwId: topicVtwId, text: topicName }],
      assessmentTopics: [{
        vtwId: topicVtwId,
        text: topicName,
        assessmentTopicLevelUps: null
      }],
      isbn,
      studyMode: true,
      type: AssessmentTypeDto.MASTERY,
      courseSectionId: course ? course.id : null
    };

    trackAction({
      action: AnalyticsAction.QUIZ_ME_ON_THIS_TOPIC_CLICK,
      props: {
        assessmentTopicVtwId: topicVtwId,
        selectedIsbn: isbn
      }
    });

    return createAssessment(assessmentConfig).then((assessmentRes: AssessmentDto) => {
      return navigateToAssessmentPlayer(
        navigateToApp,
        getParentLinkId(appLinkData, appLinkCookies),
        assessmentRes.id,
        RoutePath.EAQ_STUDENT_PERFORMANCE_DETAILS,
        assessmentId
      );
    });
  };

  truncLongText = (text: string) => {
    const textMaxLength = 50; // Number of characters before truncation
    const displayedText = text.length <= textMaxLength ? text : `${text.substring(0, textMaxLength)}...`;

    return (<div>{displayedText}</div>);
  };

  render() {
    const { messages, assessmentTopicRows, isMultipleTopic } = this.props;

    const isInstructor = ELSTokenHelper.getUserRole() === 'Ins';

    return (
      <ELSDataTable
        data={assessmentTopicRows}
        defaultSortField="masteryLevel"
        id="assessment-topics-table"
        className="c-ssa-eaq-assessment-topic-table"
        sortIconPrefix=""
      >
        <column
          field="topicName"
          header={isMultipleTopic ? messages.MULTIPLE_TOPICS : messages.TOPIC}
          sortable
          customRender={(item: AssessmentTopicTableRow) => {
            const topicName = this.truncLongText(item.topicName);
            const linDisplay = this.truncLongText(item.lineageDisplay);
            return (
              <div className="u-els-padding-1o8">
                <b>{topicName}</b>
                <p>{linDisplay}</p>
                {!isInstructor && (
                  <ELSButton
                    type={ELSButtonType.LINK}
                    size={ELSButtonSize.SMALL}
                    onClick={() => {
                      this.handleQuizMeClick(item);
                    }}
                    nativeProps={{ 'data-testid': `ssa-assessment-topic-table-quiz-me-button-${item.topicVtwId}` }}
                  >
                    {messages.QUIZ_ME_ON_THIS_TOPIC}
                  </ELSButton>
                )}
              </div>
            );
          }}
        />
        <column
          field="masteryLevel"
          header={messages.MASTERY_LEVEL}
          headerIcon={this.getMasteryLevelHeading()}
          sortable
          customRender={(item: { masteryLevel: number }) => {
            return (
              <div className="u-els-padding">
                {this.getMasteryLevelColumn(item.masteryLevel)}
              </div>
            );
          }}
        />
        <column
          field="questionsAnswered"
          header={messages.QUESTIONS_ANSWERED}
          sortable
          customRender={(item: { questionsAnswered: number }) => {
            return (
              <div className="u-els-padding">
                {item.questionsAnswered}
              </div>
            );
          }}
        />
      </ELSDataTable>
    );
  }
}

const enhancers = [connector];
const AssessmentTopicTableComponent = compose(...enhancers)(AssessmentTopicTable) as React.ComponentType<any>;
export default AssessmentTopicTableComponent;
